import { clamp, damp, easeInOutSine, hashString, lerp, seededUnit } from "../utils/math.js";

const TAU = Math.PI * 2;

export class MemoryCanvas {
  constructor(canvas, profile) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d", { alpha: true });
    this.profile = profile;
    this.width = 0;
    this.height = 0;
    this.dpr = profile.dpr;
    this.beatPulse = 0;
    this.wordPulse = 0;
    this.phrasePulse = 0;
    this.chorusBurst = 0;
    this.vocal = 0;
    this.stagePulse = 0;
    this.lyricBloom = 0;
    this.instrumentalEnergy = 0;
    this.instrumentalSpin = 0;
    this.phraseSeed = hashString("opening-memory");
    this.parameters = {
      glowIntensity: 0.72,
      archiveDensity: 0.58,
    };
    this.audienceLights = [];
    this.fragments = [];
    this.streams = [];
    this.lastTime = performance.now();
    this.resize();
    this.#createField();
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    this.width = Math.max(1, rect.width);
    this.height = Math.max(1, rect.height);
    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.#createField();
  }

  setParameter(name, value) {
    if (name === "glowIntensity") this.parameters.glowIntensity = clamp(Number(value) / 100);
    if (name === "archiveDensity") this.parameters.archiveDensity = clamp(Number(value) / 100);
  }

  onBeat(beat) {
    this.beatPulse = beat?.position === 1 ? 1 : 0.72;
    this.stagePulse = beat?.position === 1 ? 1 : 0.58;
  }

  onWord() {
    this.wordPulse = 0.54;
  }

  onPhrase(phrase) {
    this.phrasePulse = 1;
    this.lyricBloom = 1;
    if (phrase) {
      this.phraseSeed = hashString(`${phrase.startTime}:${phrase.text}`);
    }
  }

  onChorus() {
    this.chorusBurst = 1;
  }

  render(time, snapshot) {
    const delta = Math.min((time - this.lastTime) / 1000, 0.05);
    this.lastTime = time;

    const section = snapshot?.section || "intro";
    const chorusIntensity = snapshot?.chorusIntensity || 0;
    const beatProgress = snapshot?.beatProgress || 0;
    const metrics = snapshot?.metrics || { vocal: 0, valence: 0, arousal: 0 };
    const mood = snapshot?.mood || { energy: 0.3, tension: 0.2, intimacy: 0.7, release: 0, motion: 0.25 };
    const instrumentalTarget = !snapshot?.phrase && (metrics.vocal || 0) < 0.035 ? 1 : 0;

    this.beatPulse = damp(this.beatPulse, 0, 6.8, delta);
    this.stagePulse = damp(this.stagePulse, 0, 5.4, delta);
    this.wordPulse = damp(this.wordPulse, 0, 7.4, delta);
    this.phrasePulse = damp(this.phrasePulse, 0, 3.6, delta);
    this.lyricBloom = damp(this.lyricBloom, 0, 2.9, delta);
    this.chorusBurst = damp(this.chorusBurst, 0, 2.1, delta);
    this.vocal = damp(this.vocal, metrics.vocal || 0, 8, delta);
    this.instrumentalEnergy = damp(this.instrumentalEnergy, instrumentalTarget, instrumentalTarget ? 2.1 : 8.5, delta);
    if (this.instrumentalEnergy > 0.01) {
      this.instrumentalSpin =
        (this.instrumentalSpin +
          delta *
            this.instrumentalEnergy *
            (0.28 + (mood.motion ?? 0.25) * 0.34 + (mood.energy ?? 0.3) * 0.18 + this.stagePulse * 0.18 + chorusIntensity * 0.12)) %
        TAU;
    }

    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);

    this.#drawAtmosphere(ctx, time, section, chorusIntensity, metrics, mood);
    this.#drawEmotionWash(ctx, time, section, chorusIntensity, metrics, mood);
    this.#drawDataGrid(ctx, time, beatProgress, chorusIntensity);
    this.#drawConcertStage(ctx, time, section, chorusIntensity, beatProgress, metrics, mood);
    this.#drawChorusLighting(ctx, time, section, chorusIntensity, beatProgress, metrics, mood);
    this.#drawAudienceLights(ctx, time, section, chorusIntensity, beatProgress, metrics, mood);
    this.#drawLyricPresence(ctx, time, snapshot);
    this.#drawStreams(ctx, time, section, chorusIntensity, mood);
    this.#drawMemoryFragments(ctx, time, snapshot);
    this.#drawVocalWave(ctx, time, snapshot);
    this.#drawChorusGate(ctx, time, chorusIntensity);
  }

  #createField() {
    if (!this.width || !this.height) return;

    const density = this.profile.density * this.parameters.archiveDensity;
    const fragmentCount = Math.round(18 + 30 * density);
    const streamCount = Math.round(10 + 16 * density);
    const audienceCount = Math.round((this.width < 720 ? 26 : 46) * this.profile.density);

    this.audienceLights = Array.from({ length: audienceCount }, (_, index) => {
      const seed = hashString(`audience:${index}`);
      return {
        seed,
        x: seededUnit(seed, 1),
        y: 0.82 + seededUnit(seed, 2) * 0.16,
        height: 10 + seededUnit(seed, 3) * 26,
        phase: seededUnit(seed, 4) * Math.PI * 2,
        color: seededUnit(seed, 5),
      };
    });

    this.fragments = Array.from({ length: fragmentCount }, (_, index) => {
      const seed = hashString(`fragment:${index}`);
      return {
        seed,
        x: seededUnit(seed, 1),
        y: seededUnit(seed, 2),
        size: 2 + seededUnit(seed, 3) * 7,
        lane: seededUnit(seed, 4) > 0.5 ? 1 : -1,
        phase: seededUnit(seed, 5) * Math.PI * 2,
        depth: 0.25 + seededUnit(seed, 6) * 0.9,
      };
    });

    this.streams = Array.from({ length: streamCount }, (_, index) => {
      const seed = hashString(`stream:${index}`);
      return {
        seed,
        x: seededUnit(seed, 1),
        y: seededUnit(seed, 2),
        speed: 0.025 + seededUnit(seed, 3) * 0.07,
        length: 38 + seededUnit(seed, 4) * 120,
        width: 1 + seededUnit(seed, 5) * 2.5,
      };
    });
  }

  #drawAtmosphere(ctx, time, section, chorusIntensity, metrics, mood) {
    const t = time * 0.001;
    const glow = this.parameters.glowIntensity;
    const valence = metrics.valence || 0;
    const arousal = metrics.arousal || 0;
    const centerX = this.width * (0.5 + Math.sin(t * 0.17) * 0.035 + valence * 0.022);
    const centerY = this.height * (0.46 + Math.cos(t * 0.13) * 0.03 - chorusIntensity * 0.05 - Math.max(0, arousal) * 0.02);
    const radius = Math.max(this.width, this.height) * (0.5 + (mood.energy ?? 0.3) * 0.2 + chorusIntensity * 0.18);
    const warm = clamp((valence + 1) * 0.5);

    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, `rgba(105, 243, 230, ${0.07 * glow + (mood.release ?? 0) * 0.1 + (1 - warm) * 0.03})`);
    gradient.addColorStop(0.3, `rgba(255, 106, 183, ${0.032 * glow + this.beatPulse * 0.024 + Math.max(0, arousal) * 0.022 + (mood.tension ?? 0) * 0.03})`);
    gradient.addColorStop(0.66, `rgba(255, 209, 102, ${0.01 + warm * 0.024 + this.lyricBloom * 0.014 + (mood.intimacy ?? 0) * 0.012})`);
    gradient.addColorStop(1, "rgba(5, 6, 7, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = section === "chorus" ? "rgba(255, 106, 183, 0.075)" : "rgba(105, 243, 230, 0.04)";
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.restore();
  }

  #drawEmotionWash(ctx, time, section, chorusIntensity, metrics, mood) {
    const t = time * 0.001;
    const valence = metrics.valence || 0;
    const arousal = metrics.arousal || 0;
    const warm = clamp((valence + 1) * 0.5);
    const lift = clamp(this.vocal * 0.34 + Math.max(0, arousal) * 0.18 + (mood.energy ?? 0.3) * 0.24 + (mood.release ?? 0) * 0.3);
    const coolAlpha = (1 - warm) * (0.035 + lift * 0.04);
    const warmAlpha = warm * (0.028 + lift * 0.055);
    const lyricAlpha = 0.035 + this.lyricBloom * (0.06 + (mood.intimacy ?? 0.6) * 0.08) + (mood.release ?? 0) * 0.14;
    const centerX = this.width * (0.5 + Math.sin(t * 0.2) * 0.02);
    const lyricY = this.height * (section === "chorus" ? 0.44 : section === "ending" ? 0.5 : 0.48);
    const radius = Math.max(this.width, this.height) * (0.26 + (mood.release ?? 0) * 0.18 + (mood.intimacy ?? 0.6) * 0.06);

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    const cool = ctx.createLinearGradient(0, 0, this.width, this.height);
    cool.addColorStop(0, `rgba(105, 243, 230, ${coolAlpha})`);
    cool.addColorStop(0.5, "rgba(105, 243, 230, 0)");
    cool.addColorStop(1, `rgba(105, 243, 230, ${coolAlpha * 0.45})`);
    ctx.fillStyle = cool;
    ctx.fillRect(0, 0, this.width, this.height);

    const warmGradient = ctx.createLinearGradient(this.width, 0, 0, this.height);
    warmGradient.addColorStop(0, `rgba(255, 209, 102, ${warmAlpha})`);
    warmGradient.addColorStop(0.46, `rgba(255, 106, 183, ${warmAlpha * 0.55})`);
    warmGradient.addColorStop(1, "rgba(255, 209, 102, 0)");
    ctx.fillStyle = warmGradient;
    ctx.fillRect(0, 0, this.width, this.height);

    const lyricGlow = ctx.createRadialGradient(centerX, lyricY, 0, centerX, lyricY, radius);
    lyricGlow.addColorStop(0, `rgba(245, 251, 251, ${lyricAlpha})`);
    lyricGlow.addColorStop(0.32, `rgba(255, 106, 183, ${lyricAlpha * 0.38})`);
    lyricGlow.addColorStop(0.68, `rgba(105, 243, 230, ${lyricAlpha * 0.16})`);
    lyricGlow.addColorStop(1, "rgba(245, 251, 251, 0)");
    ctx.fillStyle = lyricGlow;
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.restore();
  }

  #drawDataGrid(ctx, time, beatProgress, chorusIntensity) {
    const spacing = this.width < 720 ? 48 : 64;
    const offset = ((time * 0.012) % spacing) + beatProgress * 4;
    const alpha = 0.05 + chorusIntensity * 0.04 + this.beatPulse * 0.025;

    ctx.save();
    ctx.strokeStyle = `rgba(245, 251, 251, ${alpha})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = -spacing; x < this.width + spacing; x += spacing) {
      ctx.moveTo(x + offset, 0);
      ctx.lineTo(x + offset - this.width * 0.06, this.height);
    }
    for (let y = -spacing; y < this.height + spacing; y += spacing) {
      ctx.moveTo(0, y - offset * 0.6);
      ctx.lineTo(this.width, y - offset * 0.6 + this.height * 0.04);
    }
    ctx.stroke();
    ctx.restore();
  }

  #drawConcertStage(ctx, time, section, chorusIntensity, beatProgress, metrics, mood) {
    const t = time * 0.001;
    const preChorus = section === "pre-chorus" ? 1 : 0;
    const chorus = chorusIntensity;
    const ending = section === "ending" ? 1 : 0;
    const energy = clamp((mood.energy ?? 0.3) + preChorus * 0.12 + chorus * 0.35 + this.stagePulse * (0.08 + (mood.energy ?? 0.3) * 0.12) + this.vocal * 0.14);
    const centerX = this.width * 0.5;
    const horizonY = this.height * (0.65 - (mood.release ?? 0) * 0.075 + ending * 0.04 + (mood.intimacy ?? 0) * 0.02);
    const deckY = this.height * 0.86;
    const stageWidth = this.width * (0.48 + (mood.release ?? 0) * 0.2 + (mood.energy ?? 0.3) * 0.06);
    const stageDepth = this.height * (0.16 + (mood.release ?? 0) * 0.055);

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    this.#drawLedHalo(ctx, centerX, horizonY, stageWidth, energy, t, chorus);
    this.#drawLightTowers(ctx, horizonY, deckY, energy, t, chorus);

    if (preChorus || chorus > 0.12 || (mood.tension ?? 0) > 0.58) {
      const beamAlpha = 0.045 + preChorus * 0.06 + (mood.release ?? 0) * 0.18 + (mood.tension ?? 0) * 0.065 + this.stagePulse * 0.035;
      const sway = Math.sin(t * (0.45 + (mood.motion ?? 0.25) * 0.55)) * this.width * (0.018 + (mood.motion ?? 0.25) * 0.025);
      const beamColors = this.#spotlightColors(t, beatProgress, metrics, mood, chorus);
      this.#drawBeam(ctx, this.width * 0.12, deckY, centerX + sway, this.height * 0.13, this.width * 0.18, beamColors[0], beamAlpha);
      this.#drawBeam(ctx, this.width * 0.88, deckY, centerX - sway, this.height * 0.13, this.width * 0.18, beamColors[1], beamAlpha);
      this.#drawBeam(ctx, this.width * 0.27, deckY + 10, centerX - sway * 0.4, this.height * 0.18, this.width * 0.12, beamColors[2], beamAlpha * 0.58);
      this.#drawBeam(ctx, this.width * 0.73, deckY + 10, centerX + sway * 0.4, this.height * 0.18, this.width * 0.12, beamColors[3], beamAlpha * 0.58);
    }

    const deckGradient = ctx.createLinearGradient(0, horizonY, 0, deckY + stageDepth);
    deckGradient.addColorStop(0, `rgba(105, 243, 230, ${0.02 + energy * 0.04})`);
    deckGradient.addColorStop(0.52, `rgba(255, 106, 183, ${0.018 + chorus * 0.04})`);
    deckGradient.addColorStop(1, "rgba(245, 251, 251, 0.015)");
    ctx.fillStyle = deckGradient;
    ctx.beginPath();
    ctx.moveTo(centerX - stageWidth * 0.42, horizonY);
    ctx.lineTo(centerX + stageWidth * 0.42, horizonY);
    ctx.lineTo(centerX + stageWidth * 0.64, deckY);
    ctx.lineTo(centerX - stageWidth * 0.64, deckY);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = `rgba(245, 251, 251, ${0.08 + energy * 0.12})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(centerX - stageWidth * 0.42, horizonY);
    ctx.lineTo(centerX + stageWidth * 0.42, horizonY);
    ctx.lineTo(centerX + stageWidth * 0.64, deckY);
    ctx.lineTo(centerX - stageWidth * 0.64, deckY);
    ctx.closePath();
    ctx.stroke();

    const laneCount = this.width < 720 ? 5 : 9;
    for (let i = 0; i < laneCount; i += 1) {
      const p = laneCount === 1 ? 0.5 : i / (laneCount - 1);
      const topX = centerX + (p - 0.5) * stageWidth * 0.82;
      const bottomX = centerX + (p - 0.5) * stageWidth * 1.28;
      const active = Math.sin(beatProgress * Math.PI * 2 + i * 0.7) * 0.5 + 0.5;
      ctx.strokeStyle = `rgba(105, 243, 230, ${0.03 + active * 0.04 + chorus * 0.06})`;
      ctx.beginPath();
      ctx.moveTo(topX, horizonY);
      ctx.lineTo(bottomX, deckY);
      ctx.stroke();
    }

    ctx.strokeStyle = `rgba(255, 209, 102, ${0.08 + this.stagePulse * 0.2 + chorus * 0.16})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(centerX, horizonY + stageDepth * 0.36, stageWidth * (0.3 + chorus * 0.08), stageDepth * 0.2, 0, 0, Math.PI * 2);
    ctx.stroke();

    const apertureAlpha = 0.025 + (mood.release ?? 0) * 0.22 + this.chorusBurst * 0.12;
    if (apertureAlpha > 0.04) {
      ctx.strokeStyle = `rgba(245, 251, 251, ${apertureAlpha})`;
      ctx.lineWidth = 2;
      for (let i = 0; i < 3; i += 1) {
        const radius = stageWidth * (0.22 + i * 0.08 + (mood.release ?? 0) * 0.07);
        const ringRotation = this.instrumentalSpin * (i % 2 === 0 ? 1 : -1) * (0.72 + i * 0.16);
        const ringStart = Math.PI * (1.05 + i * 0.04) + ringRotation;
        const ringEnd = Math.PI * (1.95 - i * 0.04) + ringRotation;
        ctx.beginPath();
        ctx.arc(centerX, horizonY - this.height * 0.04, radius, ringStart, ringEnd);
        ctx.stroke();
      }
    }

    if ((metrics.arousal || 0) > 0.25 || chorus > 0.4) {
      ctx.fillStyle = `rgba(255, 106, 183, ${0.018 + chorus * 0.035})`;
      ctx.fillRect(0, horizonY - 2, this.width, 4);
    }

    ctx.restore();
  }

  #drawAudienceLights(ctx, time, section, chorusIntensity, beatProgress, metrics, mood) {
    const t = time * 0.001;
    const sectionEnergy = clamp(mood.energy ?? (section === "chorus" ? 1 : section === "pre-chorus" ? 0.62 : 0.28));
    const arousal = clamp((metrics.arousal || 0) * 0.5 + 0.5);
    const baseAlpha = 0.018 + sectionEnergy * 0.05 + (mood.release ?? 0) * 0.095 + this.stagePulse * (0.025 + sectionEnergy * 0.04);
    const horizonY = this.height * 0.78;

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    const glow = ctx.createLinearGradient(0, horizonY, 0, this.height);
    glow.addColorStop(0, "rgba(5, 6, 7, 0)");
    glow.addColorStop(0.56, `rgba(105, 243, 230, ${0.018 + chorusIntensity * 0.035})`);
    glow.addColorStop(1, `rgba(255, 106, 183, ${0.018 + sectionEnergy * 0.028})`);
    ctx.fillStyle = glow;
    ctx.fillRect(0, horizonY, this.width, this.height - horizonY);

    this.audienceLights.forEach((light, index) => {
      const wave = Math.sin(t * (1.8 + arousal * 1.2) + light.phase + beatProgress * Math.PI * 2);
      const bounce = (wave * 0.5 + 0.5) * (5 + chorusIntensity * 12 + this.stagePulse * 6);
      const x = light.x * this.width + Math.sin(t * 0.7 + light.phase) * 4;
      const y = light.y * this.height - bounce;
      const height = light.height * (0.48 + sectionEnergy * 0.52 + (mood.release ?? 0) * 0.36 + this.stagePulse * 0.28);
      const alpha = baseAlpha * (0.7 + seededUnit(light.seed, 8) * 0.7);
      const color =
        light.color > 0.74
          ? "255, 209, 102"
          : light.color > 0.46
            ? "255, 106, 183"
            : "105, 243, 230";

      ctx.strokeStyle = `rgba(${color}, ${alpha})`;
      ctx.lineWidth = this.width < 720 ? 1.2 : 1.6;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.sin(t + index) * 2, y - height);
      ctx.stroke();

      if ((mood.release ?? 0) > 0.7 && index % 3 === 0) {
        ctx.fillStyle = `rgba(${color}, ${alpha * 0.5})`;
        ctx.beginPath();
        ctx.arc(x, y - height, 2.2 + this.stagePulse * 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    ctx.restore();
  }

  #drawLyricPresence(ctx, time, snapshot) {
    const section = snapshot?.section || "intro";
    const chorusIntensity = snapshot?.chorusIntensity || 0;
    const metrics = snapshot?.metrics || { vocal: 0, valence: 0, arousal: 0 };
    const mood = snapshot?.mood || { energy: 0.3, intimacy: 0.7, release: 0, tension: 0.2 };
    const t = time * 0.001;
    const warm = clamp(((metrics.valence || 0) + 1) * 0.5);
    const centerX = this.width * 0.5;
    const centerY = this.height * (section === "chorus" ? 0.47 : section === "ending" ? 0.52 : 0.5);
    const phrasePulse = this.lyricBloom + this.wordPulse * 0.35;
    const width = this.width * (0.5 + (mood.release ?? 0) * 0.26 + (mood.intimacy ?? 0.7) * 0.08);
    const height = this.height * (0.1 + (mood.release ?? 0) * 0.1 + (mood.intimacy ?? 0.7) * 0.04);
    const alpha = 0.04 + phrasePulse * (0.045 + (mood.intimacy ?? 0.7) * 0.055) + (mood.release ?? 0) * 0.16 + this.vocal * 0.04;

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    const glow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, width * 0.56);
    glow.addColorStop(0, `rgba(245, 251, 251, ${alpha})`);
    glow.addColorStop(0.2, warm > 0.52 ? `rgba(255, 209, 102, ${alpha * 0.42})` : `rgba(105, 243, 230, ${alpha * 0.42})`);
    glow.addColorStop(0.72, `rgba(255, 106, 183, ${alpha * 0.18})`);
    glow.addColorStop(1, "rgba(245, 251, 251, 0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, this.width, this.height);

    const lineAlpha = 0.08 + phrasePulse * 0.18 + chorusIntensity * 0.16;
    const seedOffset = seededUnit(this.phraseSeed, 3) * 18 - 9;
    ctx.strokeStyle = `rgba(245, 251, 251, ${lineAlpha})`;
    ctx.lineWidth = (mood.release ?? 0) > 0.6 ? 2 : 1;
    ctx.beginPath();
    ctx.ellipse(
      centerX + seedOffset,
      centerY + height * 0.58,
      width * (0.22 + (mood.release ?? 0) * 0.12 + (mood.intimacy ?? 0.7) * 0.04),
      height * (0.14 + this.vocal * 0.06 + (mood.intimacy ?? 0.7) * 0.04),
      Math.sin(t * 0.18) * 0.03,
      0,
      Math.PI * 2,
    );
    ctx.stroke();

    if ((mood.release ?? 0) > 0.58) {
      ctx.strokeStyle = `rgba(255, 209, 102, ${0.08 + phrasePulse * 0.16 + this.stagePulse * 0.08})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(centerX - width * 0.34, centerY + height * 0.7);
      ctx.quadraticCurveTo(centerX, centerY + height * (0.88 + Math.sin(t * 1.4) * 0.02), centerX + width * 0.34, centerY + height * 0.7);
      ctx.stroke();
    }

    ctx.restore();
  }

  #drawLedHalo(ctx, centerX, horizonY, stageWidth, energy, time, chorusIntensity) {
    const baseRadius = stageWidth * (0.28 + chorusIntensity * 0.1);
    const ringCount = 4;

    for (let ring = 0; ring < ringCount; ring += 1) {
      const radius = baseRadius * (0.72 + ring * 0.22);
      const yRadius = radius * (0.36 + ring * 0.025);
      const alpha = 0.035 + energy * 0.045 + chorusIntensity * 0.04 - ring * 0.004;
      const start = Math.PI * (1.02 + Math.sin(time * 0.22 + ring) * 0.02);
      const end = Math.PI * (1.98 - Math.cos(time * 0.18 + ring) * 0.02);
      ctx.strokeStyle =
        ring % 2 === 0
          ? `rgba(105, 243, 230, ${alpha})`
          : `rgba(255, 106, 183, ${alpha * 0.86})`;
      ctx.lineWidth = ring === ringCount - 1 ? 2 : 1;
      ctx.beginPath();
      ctx.ellipse(centerX, horizonY - this.height * 0.045, radius, yRadius, 0, start, end);
      ctx.stroke();
    }
  }

  #drawLightTowers(ctx, horizonY, deckY, energy, time, chorusIntensity) {
    const towerHeight = Math.max(120, this.height * (0.36 + chorusIntensity * 0.08));
    const towerTop = horizonY - towerHeight;
    const positions = [0.1, 0.18, 0.82, 0.9];

    positions.forEach((position, index) => {
      const x = this.width * position;
      const side = position < 0.5 ? -1 : 1;
      ctx.strokeStyle = `rgba(245, 251, 251, ${0.055 + energy * 0.08})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, deckY);
      ctx.lineTo(x + side * this.width * 0.025, towerTop);
      ctx.stroke();

      const nodes = this.width < 720 ? 4 : 6;
      for (let node = 0; node < nodes; node += 1) {
        const p = node / Math.max(1, nodes - 1);
        const y = deckY + (towerTop - deckY) * p;
        const pulse = Math.sin(time * 3.2 + node * 1.7 + index) * 0.5 + 0.5;
        const alpha = 0.06 + pulse * 0.08 + this.stagePulse * 0.16 + chorusIntensity * 0.12;
        ctx.fillStyle =
          (node + index) % 2 === 0
            ? `rgba(105, 243, 230, ${alpha})`
            : `rgba(255, 106, 183, ${alpha})`;
        ctx.fillRect(x - 2, y - 8, 4, 16);
      }
    });
  }

  #drawBeam(ctx, fromX, fromY, toX, toY, width, color, alpha) {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const length = Math.hypot(dx, dy) || 1;
    const nx = (-dy / length) * width * 0.5;
    const ny = (dx / length) * width * 0.5;
    const gradient = ctx.createLinearGradient(fromX, fromY, toX, toY);
    gradient.addColorStop(0, `rgba(${color}, 0)`);
    gradient.addColorStop(0.18, `rgba(${color}, ${alpha})`);
    gradient.addColorStop(0.46, `rgba(${color}, ${alpha * 0.42})`);
    gradient.addColorStop(1, `rgba(${color}, 0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(fromX - nx * 0.16, fromY - ny * 0.16);
    ctx.lineTo(fromX + nx * 0.16, fromY + ny * 0.16);
    ctx.lineTo(toX + nx, toY + ny);
    ctx.lineTo(toX - nx, toY - ny);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = `rgba(${color}, ${alpha * 0.72})`;
    ctx.beginPath();
    ctx.arc(fromX, fromY, Math.max(3, width * 0.032), 0, Math.PI * 2);
    ctx.fill();
  }

  #spotlightColors(time, beatProgress, metrics, mood, chorusIntensity) {
    const warm = clamp(((metrics.valence || 0) + 1) * 0.5);
    const energy = mood.energy ?? 0.3;
    const tension = mood.tension ?? 0.2;
    const release = mood.release ?? 0;
    const motion = mood.motion ?? 0.25;
    const emotionalShift = time * (0.18 + motion * 0.5 + energy * 0.16) + beatProgress * (0.9 + this.stagePulse * 0.85);

    const palette =
      chorusIntensity > 0.45 || release > 0.64
        ? [
            [255, 106, 183],
            [255, 209, 102],
            [105, 243, 230],
            [178, 118, 255],
            [245, 251, 251],
          ]
        : warm > 0.56
          ? [
              [255, 209, 102],
              [255, 146, 96],
              [255, 106, 183],
              [245, 251, 251],
            ]
          : tension > 0.5
            ? [
                [105, 243, 230],
                [122, 172, 255],
                [178, 118, 255],
                [255, 106, 183],
              ]
            : [
                [105, 243, 230],
                [245, 251, 251],
                [255, 209, 102],
                [255, 106, 183],
              ];

    return [0, 1, 2, 3].map((index) => {
      const seededOffset = seededUnit(this.phraseSeed, index + 12) * 0.46;
      const color = this.#paletteColor(palette, emotionalShift + index * 0.82 + seededOffset);
      return this.#rgbString(color);
    });
  }

  #paletteColor(palette, phase) {
    const wrapped = ((phase % palette.length) + palette.length) % palette.length;
    const index = Math.floor(wrapped);
    const nextIndex = (index + 1) % palette.length;
    const amount = easeInOutSine(wrapped - index);
    return [
      lerp(palette[index][0], palette[nextIndex][0], amount),
      lerp(palette[index][1], palette[nextIndex][1], amount),
      lerp(palette[index][2], palette[nextIndex][2], amount),
    ];
  }

  #rgbString(color) {
    return color.map((channel) => Math.round(channel)).join(", ");
  }

  #drawStreams(ctx, time, section, chorusIntensity, mood) {
    const t = time * 0.001;
    const speedBoost = 0.6 + (mood.motion ?? 0.25) * 1.7 + (section === "pre-chorus" ? 0.24 : 0);

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    this.streams.forEach((stream) => {
      const x = stream.x * this.width;
      const travel = (stream.y + t * stream.speed * speedBoost) % 1;
      const y = travel * (this.height + stream.length) - stream.length;
      const alpha = 0.035 + stream.width * 0.012 + (mood.energy ?? 0.3) * 0.055 + chorusIntensity * 0.05 + this.beatPulse * 0.022;

      ctx.strokeStyle =
        seededUnit(stream.seed, 7) > 0.77
          ? `rgba(255, 106, 183, ${alpha})`
          : `rgba(105, 243, 230, ${alpha})`;
      ctx.lineWidth = stream.width;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.sin(t + stream.seed) * 18, y + stream.length);
      ctx.stroke();

      if (seededUnit(stream.seed, 8) > 0.58) {
        ctx.fillStyle = `rgba(245, 251, 251, ${alpha * 0.85})`;
        ctx.fillRect(x - stream.width, y + stream.length * 0.35, stream.width * 5, 2);
      }
    });
    ctx.restore();
  }

  #drawMemoryFragments(ctx, time, snapshot) {
    const t = time * 0.001;
    const chorusIntensity = snapshot?.chorusIntensity || 0;
    const mood = snapshot?.mood || { energy: 0.3, motion: 0.25, release: 0, intimacy: 0.7 };
    const centerX = this.width * 0.5;
    const centerY = this.height * 0.5;
    const orbit = 0.65 + (mood.motion ?? 0.25) * 0.62 + (mood.release ?? 0) * 0.32;

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    this.fragments.forEach((fragment, index) => {
      const phase = fragment.phase + t * (0.035 + (mood.motion ?? 0.25) * 0.12 + fragment.depth * 0.04);
      const drift = Math.sin(phase) * 0.035 * orbit;
      const x = (fragment.x + drift * fragment.lane + chorusIntensity * (fragment.x - 0.5) * 0.08) * this.width;
      const y =
        (fragment.y + Math.cos(phase * 0.8) * 0.028 - chorusIntensity * 0.035 * fragment.depth) *
        this.height;
      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.hypot(dx, dy);
      const focus = clamp(1 - distance / Math.max(this.width, this.height));
      const pulse = this.beatPulse * (0.35 + seededUnit(fragment.seed, 9) * 0.65);
      const alpha = (0.025 + focus * 0.065 + pulse * (0.05 + (mood.energy ?? 0.3) * 0.07) + (mood.release ?? 0) * 0.09 + (mood.intimacy ?? 0.7) * 0.025) * fragment.depth;
      const size = fragment.size * (0.82 + pulse * 0.45 + (mood.release ?? 0) * 0.8);

      ctx.fillStyle =
        index % 7 === 0
          ? `rgba(255, 209, 102, ${alpha})`
          : index % 5 === 0
            ? `rgba(255, 106, 183, ${alpha})`
            : `rgba(105, 243, 230, ${alpha})`;
      ctx.fillRect(x - size * 0.5, y - size * 0.5, size * (1.8 + fragment.depth), Math.max(1, size * 0.5));

      if ((mood.release ?? 0) > 0.42 && index % 4 === 0) {
        ctx.strokeStyle = `rgba(105, 243, 230, ${alpha * 0.28})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(centerX + dx * 0.18, centerY + dy * 0.18);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    });
    ctx.restore();
  }

  #drawVocalWave(ctx, time, snapshot) {
    const metrics = snapshot?.metrics || { vocal: 0, arousal: 0 };
    const chorusIntensity = snapshot?.chorusIntensity || 0;
    const mood = snapshot?.mood || { energy: 0.3, release: 0, tension: 0.2, intimacy: 0.7 };
    const t = time * 0.001;
    const centerY = this.height * (0.58 + chorusIntensity * 0.05);
    const width = this.width * (0.44 + (mood.release ?? 0) * 0.34 + (mood.intimacy ?? 0.7) * 0.08);
    const left = (this.width - width) * 0.5;
    const amplitude = (10 + (mood.energy ?? 0.3) * 30 + (mood.release ?? 0) * 34 + this.vocal * 34 + (mood.tension ?? 0.2) * 12) * (this.profile.reducedMotion ? 0.45 : 1);
    const lines = 3;

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    for (let line = 0; line < lines; line += 1) {
      const lineAlpha = 0.035 + line * 0.022 + this.vocal * 0.08 + (mood.release ?? 0) * 0.1 + (mood.intimacy ?? 0.7) * 0.025;
      ctx.strokeStyle =
        line === 1
          ? `rgba(255, 106, 183, ${lineAlpha})`
          : `rgba(105, 243, 230, ${lineAlpha})`;
      ctx.lineWidth = line === 1 ? 2 : 1;
      ctx.beginPath();
      const steps = 84;
      for (let i = 0; i <= steps; i += 1) {
        const p = i / steps;
        const x = left + p * width;
        const wave =
          Math.sin(p * Math.PI * (4 + line) + t * (1.1 + line * 0.3)) *
          Math.sin(p * Math.PI) *
          amplitude *
          (0.42 + line * 0.24 + (metrics.arousal || 0) * 0.08);
        const y = centerY + wave + (line - 1) * 16;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  #drawChorusGate(ctx, time, chorusIntensity) {
    const intensity = clamp(chorusIntensity + this.chorusBurst * 0.8);
    if (intensity <= 0.01) return;

    const t = time * 0.001;
    const marginX = lerp(44, 18, intensity);
    const marginY = lerp(58, 24, intensity);
    const sweep = easeInOutSine((Math.sin(t * 0.65) + 1) * 0.5);

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.strokeStyle = `rgba(255, 106, 183, ${0.18 * intensity})`;
    ctx.lineWidth = 2 + intensity * 2;
    ctx.strokeRect(marginX, marginY, this.width - marginX * 2, this.height - marginY * 2);

    ctx.strokeStyle = `rgba(105, 243, 230, ${0.18 * intensity})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.width * sweep, marginY);
    ctx.lineTo(this.width * (1 - sweep), this.height - marginY);
    ctx.moveTo(this.width * (1 - sweep), marginY);
    ctx.lineTo(this.width * sweep, this.height - marginY);
    ctx.stroke();

    const flash = this.chorusBurst * 0.1 + this.beatPulse * intensity * 0.05;
    if (flash > 0.01) {
      ctx.fillStyle = `rgba(245, 251, 251, ${flash})`;
      ctx.fillRect(0, 0, this.width, this.height);
    }
    ctx.restore();
  }

  #drawChorusLighting(ctx, time, section, chorusIntensity, beatProgress, metrics, mood) {
    const intensity = clamp(chorusIntensity + this.chorusBurst * 0.9 + (section === "chorus" ? this.stagePulse * 0.16 : 0));
    if (intensity <= 0.025) return;

    const t = time * 0.001;
    const centerX = this.width * 0.5;
    const lyricY = this.height * 0.42;
    const warm = clamp(((metrics.valence || 0) + 1) * 0.5);
    const colors = this.#spotlightColors(t * 1.25, beatProgress, metrics, mood, intensity);
    const rayCount = this.width < 720 ? 5 : 7;
    const radius = Math.max(this.width, this.height) * (0.18 + intensity * 0.22);

    ctx.save();
    ctx.globalCompositeOperation = "screen";

    const bloom = ctx.createRadialGradient(centerX, lyricY, 0, centerX, lyricY, radius);
    bloom.addColorStop(0, `rgba(245, 251, 251, ${0.075 * intensity + this.chorusBurst * 0.08})`);
    bloom.addColorStop(0.24, warm > 0.52 ? `rgba(255, 209, 102, ${0.09 * intensity})` : `rgba(105, 243, 230, ${0.08 * intensity})`);
    bloom.addColorStop(0.62, `rgba(255, 106, 183, ${0.06 * intensity})`);
    bloom.addColorStop(1, "rgba(245, 251, 251, 0)");
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, this.width, this.height);

    for (let i = 0; i < rayCount; i += 1) {
      const p = rayCount === 1 ? 0.5 : i / (rayCount - 1);
      const sourceX = this.width * (0.08 + p * 0.84);
      const sourceY = this.height * (0.98 - (i % 2) * 0.05);
      const targetX = centerX + Math.sin(t * 0.72 + i * 0.62 + beatProgress * Math.PI) * this.width * (0.08 + intensity * 0.045);
      const targetY = this.height * (0.12 + seededUnit(this.phraseSeed, i + 20) * 0.13);
      const width = this.width * (0.08 + intensity * 0.045);
      const alpha = 0.028 + intensity * 0.07 + this.stagePulse * 0.035 + this.chorusBurst * 0.045;
      this.#drawBeam(ctx, sourceX, sourceY, targetX, targetY, width, colors[i % colors.length], alpha);
    }

    ctx.strokeStyle = `rgba(255, 209, 102, ${0.1 * intensity + this.stagePulse * 0.08})`;
    ctx.lineWidth = 1.5 + intensity * 1.4;
    ctx.beginPath();
    ctx.ellipse(centerX, lyricY + this.height * 0.04, this.width * (0.23 + intensity * 0.08), this.height * (0.035 + intensity * 0.028), Math.sin(t * 0.38) * 0.04, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();
  }
}
